import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import { Form as FormikForm, Formik } from 'formik';
import * as React from 'react';
import { type OnSubmit, type ValidationSchema } from '../../design-system';
import type { FormFields, FormValues } from '../../utils/formUtils';
import { FormActions } from './components/FormActions';
import { FormEditable } from './components/FormEditable';
import { FormLoading } from './components/FormLoading';
import { FormReadonly } from './components/FormReadonly';

const defaultLabels = {
  edit: 'Edit',
  cancel: 'Cancel',
  save: 'Save',
};

type FormProps<Values extends FormValues> = {
  fields: FormFields<Values>;
  initialValues: Values;
  validationSchema: ValidationSchema;
  onSubmit: OnSubmit<Values>;
  isLoading?: boolean;
  isEditing?: boolean;
  setIsEditing?: (prev: boolean) => void;
  labels?: {
    edit?: string;
    cancel?: string;
    save?: string;
  };
};

export function Form<Values extends FormValues>({
  fields,
  initialValues,
  isEditing,
  isLoading,
  labels,
  onSubmit,
  setIsEditing,
  validationSchema,
}: FormProps<Values>): React.ReactElement {
  const Label = { ...defaultLabels, ...labels };

  const onEdit = React.useCallback(() => {
    if (setIsEditing) {
      setIsEditing(true);
    }
  }, [setIsEditing]);

  const onCancel = React.useCallback(() => {
    if (setIsEditing) {
      setIsEditing(false);
    }
  }, [setIsEditing]);

  if (isLoading) {
    return (
      <>
        <FormLoading fields={fields} />
        {setIsEditing && (
          <FormActions>
            <Skeleton width="80px" height="30px" />
          </FormActions>
        )}
      </>
    );
  }

  if (isEditing) {
    return (
      <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
        {({ errors, isSubmitting, touched }) => (
          <FormikForm>
            <FormEditable disabled={isSubmitting} errors={errors} fields={fields} touched={touched} />
            <FormActions>
              {setIsEditing && (
                <>
                  <Button color="inherit" disabled={isSubmitting} onClick={onCancel} type="button" variant="contained">
                    {Label.cancel}
                  </Button>
                  <Box ml={2} />
                </>
              )}
              <Button color="primary" disabled={isSubmitting} type="submit" variant="contained">
                {isSubmitting ? <CircularProgress color="inherit" size={24} /> : Label.save}
              </Button>
            </FormActions>
          </FormikForm>
        )}
      </Formik>
    );
  }

  return (
    <>
      <FormReadonly<Values> fields={fields} initialValues={initialValues} />
      {setIsEditing && (
        <FormActions>
          <Button onClick={onEdit} size="small" variant="outlined">
            {Label.edit}
          </Button>
        </FormActions>
      )}
    </>
  );
}
